import type { RenderScene } from './renderingAdapter';
import type { InspectedFeature } from '../popups/inspection';
import type { MapLibrePopupLayer } from './mapLibreRenderPlan';

export interface MapLibrePopupFeatureLike {
  readonly id?: unknown;
  readonly properties?: unknown;
  readonly geometry?: unknown;
  readonly layer?: {
    readonly id?: unknown;
  };
}

interface NormalisedPopupCandidate extends InspectedFeature {
  readonly stackIndex: number;
  readonly queryIndex: number;
  readonly renderLayerId: string;
  readonly rendererFeatureId: string | number | null;
}

export interface ResolvedMapLibrePopupFeature {
  readonly inspection: InspectedFeature;
  readonly renderLayerId: string;
  readonly rendererFeatureId: string | number | null;
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function fallbackFeatureKey(feature: MapLibrePopupFeatureLike, queryIndex: number): string {
  try {
    return `fallback:${JSON.stringify([feature.geometry, feature.properties])}`;
  } catch {
    return `fallback:${queryIndex}`;
  }
}

function featureKey(feature: MapLibrePopupFeatureLike, queryIndex: number): string {
  return typeof feature.id === 'string' || typeof feature.id === 'number'
    ? `${typeof feature.id}:${String(feature.id)}`
    : fallbackFeatureKey(feature, queryIndex);
}

function snapshotProperties(
  properties: unknown,
  fieldNames: ReadonlySet<string>,
): Readonly<Record<string, unknown>> | null {
  if (!isRecord(properties)) return null;
  const snapshot: Record<string, unknown> = {};
  for (const field of fieldNames) {
    if (!Object.hasOwn(properties, field)) continue;
    const value = properties[field];
    if (
      value === null ||
      typeof value === 'string' ||
      typeof value === 'boolean' ||
      (typeof value === 'number' && Number.isFinite(value))
    ) {
      snapshot[field] = value;
    }
  }
  return snapshot;
}

function normalisedPopupCandidates(
  features: readonly MapLibrePopupFeatureLike[],
  popupLayers: readonly MapLibrePopupLayer[],
  scene: RenderScene,
  screenPoint: InspectedFeature['screenPoint'],
): readonly NormalisedPopupCandidate[] {
  const mappingByRenderLayer = new Map(
    popupLayers.map((mapping) => [mapping.renderLayerId, mapping]),
  );
  const layersById = new Map(scene.layers.map((layer) => [layer.id, layer]));
  const seen = new Set<string>();
  const candidates: NormalisedPopupCandidate[] = [];

  features.forEach((feature, queryIndex) => {
    const renderLayerId = feature.layer?.id;
    if (typeof renderLayerId !== 'string') return;
    const mapping = mappingByRenderLayer.get(renderLayerId);
    if (!mapping) return;
    const layer = layersById.get(mapping.layerId);
    if (!layer) return;
    const key = featureKey(feature, queryIndex);
    const deduplicationKey = `${mapping.layerId}\u0000${key}`;
    if (seen.has(deduplicationKey)) return;
    seen.add(deduplicationKey);
    const fieldNames = new Set([
      ...layer.popup.fields.map((field) => field.field),
      ...(layer.popup.titleField ? [layer.popup.titleField] : []),
    ]);
    candidates.push({
      layerId: mapping.layerId,
      sourceId: mapping.sourceId,
      featureKey: key,
      screenPoint,
      properties: snapshotProperties(feature.properties, fieldNames),
      stackIndex: mapping.stackIndex,
      queryIndex,
      renderLayerId,
      rendererFeatureId:
        typeof feature.id === 'string' || typeof feature.id === 'number' ? feature.id : null,
    });
  });

  return candidates.sort(
    (left, right) => left.stackIndex - right.stackIndex || left.queryIndex - right.queryIndex,
  );
}

function inspectionForCandidate(candidate: NormalisedPopupCandidate): InspectedFeature {
  return {
    layerId: candidate.layerId,
    sourceId: candidate.sourceId,
    featureKey: candidate.featureKey,
    screenPoint: candidate.screenPoint,
    properties: candidate.properties,
  };
}

export function canonicalPopupCandidates(
  features: readonly MapLibrePopupFeatureLike[],
  popupLayers: readonly MapLibrePopupLayer[],
  scene: RenderScene,
  screenPoint: InspectedFeature['screenPoint'],
): readonly InspectedFeature[] {
  return normalisedPopupCandidates(features, popupLayers, scene, screenPoint).map(
    inspectionForCandidate,
  );
}

export function resolveMapLibrePopupFeature(
  features: readonly MapLibrePopupFeatureLike[],
  popupLayers: readonly MapLibrePopupLayer[],
  scene: RenderScene,
  screenPoint: InspectedFeature['screenPoint'],
): ResolvedMapLibrePopupFeature | null {
  const candidate = normalisedPopupCandidates(features, popupLayers, scene, screenPoint)[0];
  return candidate
    ? {
        inspection: inspectionForCandidate(candidate),
        renderLayerId: candidate.renderLayerId,
        rendererFeatureId: candidate.rendererFeatureId,
      }
    : null;
}

export function resolvePopupFeature(
  features: readonly MapLibrePopupFeatureLike[],
  popupLayers: readonly MapLibrePopupLayer[],
  scene: RenderScene,
  screenPoint: InspectedFeature['screenPoint'],
): InspectedFeature | null {
  return resolveMapLibrePopupFeature(features, popupLayers, scene, screenPoint)?.inspection ?? null;
}
